'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useDebouncedValue } from '@kit/ui/hooks/use-debounce';

import {
  GLOBAL_SEARCH_MIN_CHARS,
  GLOBAL_SEARCH_OVERLAY_LIMIT,
  runGlobalSearch,
  type GlobalSearchPage,
} from './global-search-service';

export interface UseGlobalSearchQuery {
  query: string;
  setQuery: (value: string) => void;
  /** The debounced, trimmed query actually sent to the RPC. */
  debouncedQuery: string;
  /** True once the debounced query clears the 2-char floor (R2). */
  enabled: boolean;
  page: GlobalSearchPage | undefined;
  isLoading: boolean;
  isFetching: boolean;
}

/**
 * The overlay's data hook: owns the query string, debounces it 250ms, gates on
 * the 2-character minimum, and calls `global_search` once per settled query
 * (R2). Overlay mode — no `types` — so each entity returns its top
 * `GLOBAL_SEARCH_OVERLAY_LIMIT` rows, grouped by the service.
 */
export function useGlobalSearchQuery(): UseGlobalSearchQuery {
  const client = useSupabase();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const enabled = debouncedQuery.length >= GLOBAL_SEARCH_MIN_CHARS;

  const search = useQuery({
    queryKey: ['global-search-overlay', debouncedQuery],
    enabled,
    queryFn: () =>
      runGlobalSearch(client, {
        query: debouncedQuery,
        limit: GLOBAL_SEARCH_OVERLAY_LIMIT,
      }),
  });

  return {
    query,
    setQuery,
    debouncedQuery,
    enabled,
    page: search.data,
    isLoading: search.isLoading && enabled,
    isFetching: search.isFetching,
  };
}
